import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'reducers/store';
import { PanelTask } from 'utils/accounts';
import { setTask } from 'reducers/slices/accountPanelReducer';

import {TASKS_STORAGE} from "utils/accounts";
import {debounce} from "lodash";
import {logger} from "../../utils/logger";

const DEBOUNCE_DELAY = 500;

const TaskPersistence: React.FC = () => {
    const tasks = useSelector((state: RootState) => state.accountPanel.tasks);
    const dispatch = useDispatch();
    const isInitialMount = useRef(true); // Ref to track the very first render

    // Load Tasks on Initial Mount
    useEffect(() => {
        logger("Attempting to load tasks...");
        try {
            const storedTasks = localStorage.getItem(TASKS_STORAGE);
            if (storedTasks) {
                const parsedTasks: PanelTask[] = JSON.parse(storedTasks);
                dispatch(setTask(parsedTasks));
                logger("Tasks loaded.");
            } else {
                logger("No tasks found in localStorage.");
            }
        } catch (error) {
            logger('Failed to load tasks from localStorage:', error);
        }
    }, [dispatch]); // Run only once when the component mounts

    // Debounced Save Function
    const debouncedSave = useCallback(
        debounce((tasksToSave: PanelTask[]) => {
            try {
                localStorage.setItem(TASKS_STORAGE, JSON.stringify(tasksToSave));
            } catch (error) {
                logger('Failed to save tasks to localStorage:', error);
            }
        }, DEBOUNCE_DELAY),
        [] // No dependencies, created only once
    );

    // Save Tasks When They Change
    useEffect(() => {
        // If it's the initial mount, don't save yet.
        // This prevents saving an empty or partially loaded state.
        // Set the ref to false so later runs will save.
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // On later changes, call the debounced save function.
        logger("Tasks changed, queuing save...");
        debouncedSave(tasks);

        // Cancel any pending save if component unmounts or tasks change again.
        return () => {
            debouncedSave.cancel();
        };
    }, [tasks, debouncedSave]);

    return null;
};

export default TaskPersistence;